import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import AppBar from "../../../common/AppBar.js";
import Snackbar from "../../../common/Snackbar.js";
import RenderHtml from "../../../common/AppSpecificConpnents/PartsTable/RenderHtml.js";
import {
  HeadingWithTreePath,
  generateCompleteTreePath,
} from "../../SubassemblyDetails/utils/treePathUtils.js";

import "../../SubassemblyDetails/SubassemblyDetails.css";
import { getPages, renderPreview, renderVideo } from "../plugins/utils.js";
import { getAllSectionsForPart } from "../../../redux/sectionSlice.js";
import {
  getAllAttachmentsForStep,
  getAllAttachmentsForSubstep,
  getAllCanvasesForStep,
  getAllCanvasesForSubstep,
  getAllFormsForStep,
  getAllFormsForSubstep,
  getAllRichTextsForStep,
  getAllRichTextsForSubstep,
  getAllStepsForSection,
  getAllSubstepsForStep,
  getAllVideosForStep,
  getAllVideosForSubstep,
} from "../../../redux/stepSlice.js";

import CanvasDisplay from "../../Canvas/components/canvas-core/CanvasDisplay.jsx";
const ProcedureStepMove = () => {
  const [targetSectionData, setTargetSectionData] = useState({});
  const { sections } = useSelector((state) => state.section);
  let richTextCounter = 1;
  let canvasCounter = 1;
  let videoCounter = 1;
  let formCounter = 1;
  let attachmentCounter = 1;
  const [createCopy, setCreateCopy] = useState(false);

  let richTextsCounter = {};

  const location = useLocation();
  const navigate = useNavigate();

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "",
  });

  const compareSiblingOrders = (a, b) => {
    const intA = parseInt(a, 10);
    const intB = parseInt(b, 10);
    return intA - intB;
  };

  const {
    type,
    partNumber,
    description,
    revision,
    status,
    title,
    steps,
    fromSectionId,
    fromTreeId,
    fromSiblingOrder,
    partId,
    revisionId,
    fromStepSiblingOrder,
    fromStepId,
    fromStepTreeId,
    target,
  } = location.state;

  const [selectedSectionIndex, setSelectedSectionIndex] = useState(null);
  const [selectedStepInfo, setSelectedStepInfo] = useState(null);
  const [selectionType, setSelectionType] = useState(null); // 'section' or 'step'

  const {
    stepsBySection,
    substepsByStep,
    richTexts,
    attachments,
    forms,
    canvases,
    videos,
  } = useSelector((state) => state.step);

  const showSnackbar = (message, type) => {
    setSnackbar({ open: true, message, type });
  };

  const sortedSections = [...sections];

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleCreateCopyChange = () => {
    setCreateCopy(!createCopy);
  };

  // Get the buttons to display based on selection type
  const getButtons = () => {
    const buttons = [];

    if (selectionType === "section") {
      // Only show Move Into/Copy Into button when section is selected
      buttons.push({
        label: createCopy ? "Paste Into" : "Move Into",
        onClick: handleMoveInto,
      });
    } else if (selectionType === "step") {
      // Only show Move Up/Copy Up and Move Below/Copy Below buttons when step is selected
      buttons.push({
        label: createCopy ? "Paste Above" : "Move Up",
        onClick: handleMoveUp,
      });
      buttons.push({
        label: createCopy ? "Paste Below" : "Move Below",
        onClick: handleMoveBelow,
      });
    } else {
      // Show no buttons when nothing is selected
      // No buttons shown when no checkbox is selected
    }

    return buttons;
  };

  const handleMoveUp = () => {
    if (!selectedStepInfo) {
      showSnackbar("Please select a target step to move to", "error");
      return;
    }

    // Update direction for AppBar title
    setSelectedStepInfo({
      ...selectedStepInfo,
      direction: "up",
    });

    // Get source step data to ensure we have accurate sibling order
    const sourceStepData = location.state.sourceStepData || {};
    const sourceStepOrder =
      sourceStepData.sibling_order ||
      sourceStepData.procedure_tree?.sibling_order ||
      location.state.fromStepSiblingOrder ||
      0;

    // Navigate to preview screen
    navigate("/procedure-step-move-preview", {
      state: {
        ...location.state,
        moveType: "up",
        targetSectionIndex: selectedStepInfo.sectionIndex,
        targetStepIndex: selectedStepInfo.stepIndex,
        sourceStepIndex: parseInt(title.split(" ")[1]) - 1,
        sourceSectionIndex: location.state.sourceSectionIndex,
        movingStep: true,
        revisionId,
        targetSectionData,
        partId,
        fromStepSiblingOrder: sourceStepOrder,
        selectedStepInfo,
        createCopy, // Add createCopy to navigation state
      },
    });
  };

  const handleMoveInto = () => {
    if (!selectedSectionIndex && selectedSectionIndex !== 0) {
      showSnackbar("Please select a target section to move into", "error");
      return;
    }

    // Get the highest sibling_order for steps in the target section
    const targetSection = sortedSections[selectedSectionIndex];
    const targetSectionId = targetSection.section_tree_node_id;
    const stepsInTargetSection = stepsBySection[targetSectionId] || [];

    // Calculate the highest sibling order
    let highestSiblingOrder = 0;
    if (stepsInTargetSection.length > 0) {
      highestSiblingOrder = Math.max(
        ...stepsInTargetSection.map((step) =>
          parseInt(
            step.sibling_order || step.procedure_tree?.sibling_order || 0
          )
        )
      );
    }

    // Navigate to preview screen
    navigate("/procedure-step-move-preview", {
      state: {
        ...location.state,
        moveType: "into",
        targetSectionIndex: selectedSectionIndex,
        sourceStepIndex: parseInt(title.split(" ")[1]) - 1,
        sourceSectionIndex: location.state.sourceSectionIndex,
        movingStep: true,
        revisionId,
        targetSectionData,
        partId,
        fromSectionId,
        fromTreeId,
        fromSiblingOrder,
        fromStepSiblingOrder,
        fromStepId,
        fromStepTreeId,
        createCopy,
      },
    });
  };
  const handleMoveBelow = () => {
    if (!selectedStepInfo) {
      showSnackbar("Please select a target step to move to", "error");
      return;
    }

    // Update direction for AppBar title
    setSelectedStepInfo({
      ...selectedStepInfo,
      direction: "below",
    });

    // Get source step data to ensure we have accurate sibling order
    const sourceStepData = location.state.sourceStepData || {};
    const sourceStepOrder =
      sourceStepData.sibling_order ||
      sourceStepData.procedure_tree?.sibling_order ||
      location.state.fromStepSiblingOrder ||
      0;

    // Navigate to preview screen
    navigate("/procedure-step-move-preview", {
      state: {
        ...location.state,
        moveType: "below",
        targetSectionIndex: selectedStepInfo.sectionIndex,
        targetStepIndex: selectedStepInfo.stepIndex,
        sourceStepIndex: parseInt(title.split(" ")[1]) - 1,
        sourceSectionIndex: location.state.sourceSectionIndex,
        movingStep: true,
        revisionId,
        targetSectionData,
        partId,
        fromStepSiblingOrder: fromStepSiblingOrder || sourceStepOrder,
        selectedStepInfo,
        createCopy, // Add createCopy to navigation state
      },
    });
  };

  const sourceStepNumber = parseInt(title.split(" ")[1].trim());
  const sourceSection = location.state.fromSectionId;

  const dispatch = useDispatch();

  useEffect(() => {
    (async () => {
      // setShowLoader(true);

      await dispatch(getAllSectionsForPart({ partId, revisionId }));
    })();
  }, [dispatch, partId, revisionId]);

  useEffect(() => {
    if (sections.length > 0) {
      sections.forEach(async (section, index) => {
        await dispatch(
          getAllStepsForSection({
            partId,
            revisionId,
            sectionId: section.section_tree_node_id,
          })
        );
      });
    }
  }, [sections, dispatch, partId, revisionId]);

  useEffect(() => {
    if (Object.keys(stepsBySection).length > 0) {
      Object.entries(stepsBySection).forEach(([sectionId, steps], index) => {
        steps.forEach(async (step) => {
          const { step_tree_node_id: stepId } = step;

          // // Fetch for step-level content
          await dispatch(
            getAllRichTextsForStep({ partId, revisionId, sectionId, stepId })
          );
          await dispatch(
            getAllFormsForStep({ partId, revisionId, sectionId, stepId })
          );
          await dispatch(
            getAllVideosForStep({ partId, revisionId, sectionId, stepId })
          );
          await dispatch(
            getAllAttachmentsForStep({ partId, revisionId, sectionId, stepId })
          );
          await dispatch(
            getAllCanvasesForStep({ partId, revisionId, sectionId, stepId })
          );
          // Fetch substeps (async)
          await dispatch(
            getAllSubstepsForStep({ partId, revisionId, sectionId, stepId })
          ).then((action) => {
            const substeps = action.payload?.substeps || [];
            substeps.forEach((substep) => {
              const substepId = substep.sub_step_tree_node_id;

              dispatch(
                getAllRichTextsForSubstep({
                  partId,
                  revisionId,
                  sectionId,
                  stepId,
                  substepId,
                })
              );
              dispatch(
                getAllFormsForSubstep({
                  partId,
                  revisionId,
                  sectionId,
                  stepId,
                  substepId,
                })
              );
              dispatch(
                getAllVideosForSubstep({
                  partId,
                  revisionId,
                  sectionId,
                  stepId,
                  substepId,
                })
              );
              dispatch(
                getAllAttachmentsForSubstep({
                  partId,
                  revisionId,
                  sectionId,
                  stepId,
                  substepId,
                })
              );
              dispatch(
                getAllCanvasesForSubstep({
                  partId,
                  revisionId,
                  sectionId,
                  stepId,
                  substepId,
                })
              );
            });
          });
          if (index === Object.entries(stepsBySection).length - 1) {
            // setShowLoader(false);
          }
        });
      });
    } else {
      // setShowLoader(false);
    }
  }, [sections, dispatch, partId, revisionId, stepsBySection]);

  return (
    <div>
      <AppBar
        showTablesDropdown={false}
        showBackButton={false}
        showCancelButton={true}
        showCreateCopyCheckbox={true}
        createCopy={createCopy}
        onCreateCopyChange={handleCreateCopyChange}
        title={`${createCopy ? "Copying" : "Moving"} ${title} ${
          selectionType === "section" && selectedSectionIndex !== null
            ? `into Section ${selectedSectionIndex + 1}`
            : selectionType === "step" && selectedStepInfo
            ? `${
                selectedStepInfo.direction === "up"
                  ? "above"
                  : selectedStepInfo.direction === "below"
                  ? "below"
                  : "to"
              } Step ${selectedStepInfo.globalStepNumber}`
            : ""
        }`}
        buttons={getButtons()}
      />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "20px",
          padding: "8px 0",
          flexWrap: "wrap", // for responsiveness
        }}
      >
        {/* Checkbox + Title + Description */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              margin: 0,
            }}
          >
            {/* Hide procedure checkbox */}
            <span
              style={{
                fontSize: "20px",
                fontWeight: 600,
                // Removed blue color for procedure
              }}
            >
              {type} {partNumber}
            </span>
          </label>

          {/* Description */}
          {/* <span style={{ marginLeft: "2px", marginTop: "5px", color: "#666" }}>
            Description: {description}
          </span> */}
        </div>

        {/* Right-aligned section */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "20px",
            marginLeft: "auto",
          }}
        >
          <div>
            <strong>Status: </strong>
            <span style={{ color: "orange" }}>{status}</span>
          </div>

          <div>
            <strong>Revision: </strong>
            <span>{revision}</span>
          </div>
        </div>
      </div>

      {sortedSections
        .sort((a, b) => {
          const orderA = a.sibling_order || a.procedure_tree?.sibling_order;
          const orderB = b.sibling_order || b.procedure_tree?.sibling_order;

          const orderCompare = compareSiblingOrders(orderA, orderB);
          if (orderCompare !== 0) return orderCompare;

          // Fallback: compare created_at
          const dateA = new Date(a.created_at);
          const dateB = new Date(b.created_at);
          return dateB - dateA; // latest comes first
        })
        .map((section, index) => {
          // Reset counters at the beginning of each procedure to ensure consistent numbering
          if (index === 0) {
            richTextCounter = 1;
            canvasCounter = 1;
            videoCounter = 1;
            formCounter = 1;
            attachmentCounter = 1;
            richTextsCounter = {}; // Reset the counter tracking object
          }

          // Check if this is the source section or the selected section
          const isSourceSection =
            section.section_tree_node_id === sourceSection;
          // Removed the disabled condition to allow selection of source section
          const isCheckboxDisabled = false;

          return (
            <div
              key={section.section_tree_node_id}
              style={{
                margin: "20px 0",
                clear: "both",
              }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                  clear: "both",
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedSectionIndex === index}
                  onChange={() => {
                    if (selectedSectionIndex === index) {
                      setSelectedSectionIndex(null);
                      setSelectionType(null);
                      setTargetSectionData({});
                    } else {
                      setSelectedSectionIndex(index);
                      setSelectedStepInfo(null);
                      setSelectionType("section");
                      setTargetSectionData({
                        sectionId: section.section_tree_node_id,
                        siblingOrder: section.sibling_order,
                        sectionTreeId: section.section_tree_node_id,
                      });
                    }
                  }}
                  disabled={isCheckboxDisabled}
                  style={{
                    accentColor: "#0d6efd", // Blue color for sections
                    backgroundColor: "#0d6efd",
                  }}
                />

                <h2 style={{ margin: 0, fontSize: "20px", color: "#0d6efd" }}>
                  Section {index + 1}
                </h2>
                {isSourceSection && (
                  <span
                    style={{
                      marginLeft: "12px",
                      fontSize: "14px",
                      color: "#666",
                      fontStyle: "italic",
                    }}
                  >
                    {/* (Contains step being moved) */}
                  </span>
                )}
              </label>
              <hr />
              <hr />
              <div>{section.section_name}</div>
              <div>{section.short_description}</div>

              {/* Steps for this section */}
              {(stepsBySection[section.section_tree_node_id] || []).map(
                (step, stepIndex) => {
                  const stepId = step.step_tree_node_id;

                  const stepsBeforeThisSection = sortedSections
                    .slice(0, index)
                    .reduce(
                      (acc, s) =>
                        acc +
                        (stepsBySection[s.section_tree_node_id]?.length || 0),
                      0
                    );
                  const globalStepNumber =
                    stepsBeforeThisSection + stepIndex + 1;

                  // Get all items for this step
                  const myVideos = videos[stepId] || [];
                  const myForms = forms[stepId] || [];
                  const myCanvases = canvases[stepId] || [];
                  const myRichTexts = richTexts[stepId] || [];
                  const myAttachments = attachments[stepId] || [];

                  // Store the current counter values before processing this step
                  const currentRichTextCounter = richTextCounter;
                  const currentVideoCounter = videoCounter;
                  const currentFormCounter = formCounter;
                  const currentCanvasCounter = canvasCounter;
                  const currentAttachmentCounter = attachmentCounter;

                  // Store the counter state for this step
                  richTextsCounter[stepId] = currentRichTextCounter;

                  const combinedItems = [
                    ...(richTexts[stepId] || []).map((item) => ({
                      ...item,
                      type: "richText",
                    })),
                    ...(forms[stepId] || []).map((item) => ({
                      ...item,
                      type: "form",
                    })),
                    ...(attachments[stepId] || []).map((item) => ({
                      ...item,
                      type: "attachment",
                    })),
                    ...(canvases[stepId] || []).map((item) => ({
                      ...item,
                      type: "canvas",
                    })),
                    ...(videos[stepId] || []).map((item) => ({
                      ...item,
                      type: "video",
                    })),
                  ]; // Filter out substeps or unwanted items (same as your current filter)
                  const filteredItems = combinedItems.filter(
                    (item) =>
                      !item.substep_id_ref && !item.sub_step_tree_node_id
                  );

                  filteredItems.sort((a, b) => {
                    const orderA =
                      a.sibling_order || a.procedure_tree?.sibling_order;
                    const orderB =
                      b.sibling_order || b.procedure_tree?.sibling_order;

                    const orderCompare = compareSiblingOrders(orderA, orderB);
                    if (orderCompare !== 0) return orderCompare;

                    // Fallback: compare created_at
                    const dateA = new Date(a.created_at);
                    const dateB = new Date(b.created_at);
                    return dateB - dateA; // latest comes first
                  });

                  return (
                    <div
                      key={stepId}
                      style={{ marginTop: "20px", clear: "both" }}
                    >
                      <label
                        style={{
                          display: "flex",
                          gap: "8px",
                          cursor: "pointer",
                          clear: "both",
                        }}
                      >
                        <input
                          type="checkbox"
                          onChange={() => {
                            if (
                              selectedStepInfo &&
                              selectedStepInfo.stepId === stepId
                            ) {
                              setSelectedStepInfo(null);
                              setSelectionType(null);
                            } else {
                              // Get accurate sibling order from the step data
                              const stepOrder =
                                step.sibling_order ||
                                step.procedure_tree?.sibling_order;

                              setSelectedStepInfo({
                                stepId,
                                sectionIndex: index,
                                stepIndex: stepIndex,
                                globalStepNumber,
                                direction: null,
                              });
                              setTargetSectionData({
                                sectionId: section.section_tree_node_id,
                                siblingOrder: section.sibling_order,
                                sectionTreeId: section.section_tree_node_id,
                                stepId,
                                stepTreeId: step.procedure_tree_ref,
                                stepSiblingOrder: stepOrder,
                                step: { ...step }, // Keep the full step data
                                stepIndex,
                                globalStepNumber,
                              });
                              setSelectedSectionIndex(null);
                              setSelectionType("step");
                            }
                          }}
                          checked={
                            location.state.fromStepId === stepId ||
                            (selectedStepInfo &&
                              selectedStepInfo.stepId === stepId)
                          }
                          style={
                            location.state.fromStepId === stepId
                              ? {
                                  accentColor: "#444444", // Dark grey for step being moved
                                  backgroundColor: "#444444",
                                }
                              : {
                                  accentColor: "#0d6efd", // Blue color for other steps
                                  backgroundColor: "#0d6efd",
                                }
                          }
                          disabled={false}
                        />

                        <HeadingWithTreePath
                          title={`Step ${globalStepNumber}`}
                          treePathParams={{
                            sectionIndex: index,
                            stepIndex: globalStepNumber - 1,
                            substepIndex: undefined,
                            nodeType: undefined,
                            nodeIndex: undefined,
                          }}
                          color="#0d6efd" // Blue color for all steps
                        />
                        {location.state.fromStepId === stepId && (
                          <span
                            style={{
                              marginLeft: "12px",
                              fontSize: "14px",
                              color: "#666",
                              fontStyle: "italic",
                            }}
                          >
                            {/* (Step being moved) */}
                          </span>
                        )}
                      </label>
                      <hr />
                      <p style={{ whiteSpace: 'pre-wrap' }}>{step.short_description || ""}</p>
                      <p>
                        <p>
                          {structuredClone(step.step_details)
                            .sort(
                              (a, b) =>
                                new Date(a.created_at) - new Date(b.created_at)
                            )
                            .map((myStep) => {
                              const partNumber =
                                myStep.part_info?.part_number || "";
                              const description =
                                myStep.part_info?.description || "";
                              const qtyText =
                                myStep.qty && myStep.qty != 0
                                  ? `Qty ${myStep.qty}`
                                  : "";

                              const partText = partNumber
                                ? ` (${partNumber})`
                                : "";

                              return (
                                <>
                                  {myStep.action}
                                  {partText}{" "}
                                  <RenderHtml content={description} /> {qtyText}
                                  &nbsp;
                                </>
                              );
                            })}
                        </p>
                      </p>

                      <h4>{step.bom_table_title_info?.category_name || ""}</h4>

                      <table className="table">
                        <thead>
                          <tr>
                            <th>Type</th>
                            <th>
                              {
                                step.part_number_column_title_ref_info
                                  ?.category_name
                              }{" "}
                            </th>
                            <th>Description</th>
                            <th>Qty Per</th>
                            <th>Unit</th>
                          </tr>
                        </thead>

                        <tbody>
                          {step.parts_required.map((row) => (
                            <tr>
                              <td>{row.part_info.type_info.name}</td>
                              <td>{row.part_info.part_number}</td>
                              <td>
                                <RenderHtml
                                  content={row.part_info.description}
                                />{" "}
                              </td>
                              <td>{row.qty === 0 ? "" : row.qty}</td>
                              <td>{row.part_info?.unit_info?.unit_name}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      {/* Step Content */}

                      {filteredItems.map((item, itemIndex) => {
                        switch (item.type) {
                          case "richText": {
                            const globalRichTextNumber = richTextCounter++;
                            // render richText block here
                            const label = (
                              <HeadingWithTreePath
                                title={`Rich Text ${globalRichTextNumber}`}
                                treePathParams={{
                                  sectionIndex: index,
                                  stepIndex: globalStepNumber - 1,
                                  substepIndex: undefined,
                                  nodeType: "richText",
                                  nodeIndex: globalRichTextNumber - 1,
                                }}
                              />
                            );

                            return (
                              <div
                                key={`step-richtext-${stepId}-${globalRichTextNumber}`}
                                style={{ marginTop: "20px" }}
                              >
                                <label
                                  style={{
                                    display: "flex",
                                    gap: "6px",
                                    cursor: "pointer",
                                    clear: "both",
                                  }}
                                >
                                  {location.state.fromStepId === stepId && (
                                    <input
                                      type="checkbox"
                                      checked={true}
                                      disabled={true}
                                      style={{
                                        accentColor: "#444444",
                                        backgroundColor: "#444444",
                                      }}
                                    />
                                  )}
                                  {label}
                                </label>

                                <div
                                  style={{
                                    marginLeft: "5px",
                                    marginTop: "-10px",
                                  }}
                                >
                                  {item.short_description}
                                </div>
                                <div
                                  style={{
                                    marginLeft: "4px",
                                    marginTop: "10px",
                                  }}
                                >
                                  {item.category_data?.category_name}
                                </div>
                                <div
                                  style={{
                                    fontSize: "20px",
                                    marginLeft: "4px",
                                    marginTop: "5px",
                                    clear: "both",
                                  }}
                                >
                                  <div
                                    style={{ marginTop: "-6px", clear: "both" }}
                                    dangerouslySetInnerHTML={{
                                      __html: item.rich_text_content,
                                    }}
                                  />
                                </div>
                              </div>
                            );
                          }
                          case "video": {
                            const globalVideoNumber = videoCounter++;
                            const label = (
                              <HeadingWithTreePath
                                title={`Video ${globalVideoNumber}`}
                                treePathParams={{
                                  sectionIndex: index,
                                  stepIndex: globalStepNumber - 1,
                                  substepIndex: undefined,
                                  nodeType: "video",
                                  nodeIndex: globalVideoNumber - 1,
                                }}
                              />
                            );

                            return (
                              <div
                                key={`step-video-${stepId}-${globalVideoNumber}`}
                                style={{ marginTop: "20px", clear: "both" }}
                              >
                                <label
                                  style={{
                                    display: "flex",
                                    gap: "6px",
                                    cursor: "pointer",
                                    clear: "both",
                                  }}
                                >
                                  {location.state.fromStepId === stepId && (
                                    <input
                                      type="checkbox"
                                      checked={true}
                                      disabled={true}
                                      style={{
                                        accentColor: "#444444",
                                        backgroundColor: "#444444",
                                      }}
                                    />
                                  )}
                                  {label}
                                </label>

                                {renderVideo(item)}

                                <div>{item.video_url}</div>

                                <div>{item.short_description}</div>
                              </div>
                            );
                          }

                          case "form": {
                            const globalFormNumber = formCounter++;
                            const label = (
                              <HeadingWithTreePath
                                title={`Form ${globalFormNumber}`}
                                treePathParams={{
                                  sectionIndex: index,
                                  stepIndex: globalStepNumber - 1,
                                  substepIndex: undefined,
                                  nodeType: "form",
                                  nodeIndex: globalFormNumber - 1,
                                }}
                              />
                            );
                            // render form block here
                            return (
                              <div
                                key={`step-form-${stepId}-${globalFormNumber}`}
                                style={{ clear: "both" }}
                              >
                                <label
                                  style={{
                                    display: "flex",
                                    gap: "6px",
                                    cursor: "pointer",
                                    marginTop: "20px",
                                    clear: "both",
                                  }}
                                >
                                  {location.state.fromStepId === stepId && (
                                    <input
                                      type="checkbox"
                                      checked={true}
                                      disabled={true}
                                      style={{
                                        accentColor: "#444444",
                                        backgroundColor: "#444444",
                                      }}
                                    />
                                  )}
                                  {label}
                                </label>
                                <div>{item.short_description}</div>
                              </div>
                            );
                          }
                          case "canvas": {
                            const pages = getPages(item);

                            const globalCanvasNumber = canvasCounter++;
                            const label = (
                              <HeadingWithTreePath
                                title={`Canvas ${globalCanvasNumber}`}
                                treePathParams={{
                                  sectionIndex: index,
                                  stepIndex: globalStepNumber - 1,
                                  substepIndex: undefined,
                                  nodeType: "canvas",
                                  nodeIndex: globalCanvasNumber - 1,
                                }}
                              />
                            );
                            const shapes = item.shapes || [];

                            return (
                              <div
                                key={`step-canvas-${stepId}-${globalCanvasNumber}`}
                                style={{ marginTop: "40px", clear: "both" }}
                              >
                                <label
                                  style={{
                                    display: "flex",
                                    gap: "6px",
                                    cursor: "pointer",
                                    clear: "both",
                                  }}
                                >
                                  {location.state.fromStepId === stepId && (
                                    <input
                                      type="checkbox"
                                      checked={true}
                                      disabled={true}
                                      style={{
                                        accentColor: "#444444",
                                        backgroundColor: "#444444",
                                      }}
                                    />
                                  )}
                                  {label}
                                </label>

                                <div>{item.short_description}</div>

                                {/* Display all pages vertically */}
                                <CanvasDisplay pages={pages} />
                              </div>
                            );
                          }
                          case "attachment": {
                            const attachmentList = item.attachments || [];
                            if (attachmentList.length === 0) return null;

                            // Increment counter for each attachment
                            const globalAttachmentNumber = attachmentCounter++;
                            const label = (
                              <HeadingWithTreePath
                                title={`Attachment ${globalAttachmentNumber}`}
                                treePathParams={{
                                  sectionIndex: index,
                                  stepIndex: globalStepNumber - 1,
                                  substepIndex: undefined,
                                  nodeType: "attachment",
                                  nodeIndex: globalAttachmentNumber - 1,
                                }}
                              />
                            );

                            return (
                              <div
                                key={`step-attachment-${stepId}-${globalAttachmentNumber}`}
                                style={{ clear: "both" }}
                              >
                                <label
                                  style={{
                                    display: "flex",
                                    gap: "6px",
                                    cursor: "pointer",
                                    marginTop: "20px",
                                    clear: "both",
                                  }}
                                >
                                  {location.state.fromStepId === stepId && (
                                    <input
                                      type="checkbox"
                                      checked={true}
                                      disabled={true}
                                      style={{
                                        accentColor: "#444444",
                                        backgroundColor: "#444444",
                                      }}
                                    />
                                  )}
                                  {label}
                                </label>
                                <div>{item.short_description}</div>
                                {attachmentList.map((attachment, i) => (
                                  <div
                                    key={`step-attachment-item-${stepId}-${globalAttachmentNumber}-${i}`}
                                    style={{
                                      marginLeft: "20px",
                                    }}
                                  >
                                    {renderPreview(attachment)}
                                  </div>
                                ))}
                              </div>
                            );
                          }
                          default:
                            return null;
                        }
                      })}

                      {/* Substeps */}
                      {(substepsByStep[stepId] || []).map(
                        (substep, substepIndex) => {
                          const substepId = substep.sub_step_tree_node_id;

                          // Get all items for this substep
                          const substepRichTexts = richTexts[substepId] || [];
                          const substepForms = forms[substepId] || [];
                          const substepAttachments =
                            attachments[substepId] || [];
                          const substepCanvases = canvases[substepId] || [];
                          const substepVideos = videos[substepId] || [];

                          const combinedItems = [
                            ...substepRichTexts.map((item) => ({
                              ...item,
                              type: "richText",
                            })),
                            ...substepForms.map((item) => ({
                              ...item,
                              type: "form",
                            })),
                            ...substepAttachments.map((item) => ({
                              ...item,
                              type: "attachment",
                            })),
                            ...substepCanvases.map((item) => ({
                              ...item,
                              type: "canvas",
                            })),
                            ...substepVideos.map((item) => ({
                              ...item,
                              type: "video",
                            })),
                          ];

                          const filteredItems = combinedItems;

                          filteredItems.sort((a, b) => {
                            const orderA =
                              a.sibling_order ||
                              a.procedure_tree?.sibling_order;
                            const orderB =
                              b.sibling_order ||
                              b.procedure_tree?.sibling_order;

                            const orderCompare = compareSiblingOrders(
                              orderA,
                              orderB
                            );
                            if (orderCompare !== 0) return orderCompare;

                            // Fallback: compare created_at
                            const dateA = new Date(a.created_at);
                            const dateB = new Date(b.created_at);
                            return dateB - dateA; // latest comes first
                          });

                          // Global step number (same logic you already use for steps)
                          const stepsBeforeThisOne =
                            sortedSections
                              .slice(0, index)
                              .reduce((sectionAcc, s) => {
                                const steps =
                                  stepsBySection[s.section_tree_node_id] || [];
                                return sectionAcc + steps.length;
                              }, 0) + stepIndex;

                          const globalStepNumber = stepsBeforeThisOne + 1;

                          // Final substep label like Substep 5A
                          const substepLetter = String.fromCharCode(
                            65 + substepIndex
                          ); // A, B, C, ...

                          const globalSubstepLabel = `Substep ${globalStepNumber}${substepLetter}`;

                          return (
                            <div
                              key={substepId}
                              style={{ marginTop: "20px", clear: "both" }}
                            >
                              <div>
                                <label
                                  style={{
                                    display: "flex",
                                    gap: "8px",
                                    cursor: "pointer",
                                    clear: "both",
                                  }}
                                >
                                  {location.state.fromStepId === stepId && (
                                    <input
                                      type="checkbox"
                                      checked={true}
                                      disabled={true}
                                      style={{
                                        accentColor: "#444444",
                                        backgroundColor: "#444444",
                                      }}
                                    />
                                  )}
                                  <HeadingWithTreePath
                                    title={globalSubstepLabel}
                                    treePathParams={{
                                      sectionIndex: index,
                                      stepIndex: globalStepNumber - 1,
                                      substepIndex: substepIndex,
                                      nodeType: undefined,
                                      nodeIndex: undefined,
                                    }} // Blue color for all substeps
                                  />
                                </label>

                                <div style={{ whiteSpace: 'pre-wrap' }}>{substep.short_description}</div>
                              </div>

                              {/* Substep Rich Texts */}
                              {filteredItems.map((item, itemIndex) => {
                                switch (item.type) {
                                  case "richText": {
                                    const globalRichTextNumber =
                                      richTextCounter++; // ✅ use then increment
                                    // render richText block here
                                    const label = (
                                      <HeadingWithTreePath
                                        title={`Rich Text ${globalRichTextNumber}`}
                                        treePathParams={{
                                          sectionIndex: index,
                                          stepIndex: globalStepNumber - 1,
                                          substepIndex: substepIndex,
                                          nodeType: "richText",
                                          nodeIndex: globalRichTextNumber - 1,
                                        }}
                                      />
                                    );

                                    return (
                                      <div
                                        key={`substep-richtext-${substepId}-${globalRichTextNumber}`}
                                        style={{
                                          marginTop: "20px",
                                          clear: "both",
                                        }}
                                      >
                                        <label
                                          style={{
                                            display: "flex",
                                            gap: "6px",
                                            cursor: "pointer",
                                            clear: "both",
                                          }}
                                        >
                                          {location.state.fromStepId ===
                                            stepId && (
                                            <input
                                              type="checkbox"
                                              checked={true}
                                              disabled={true}
                                              style={{
                                                accentColor: "#444444",
                                                backgroundColor: "#444444",
                                              }}
                                            />
                                          )}
                                          {label}
                                        </label>

                                        <div
                                          style={{
                                            marginLeft: "5px",
                                            marginTop: "-10px",
                                          }}
                                        >
                                          {item.short_description}
                                        </div>
                                        <div
                                          style={{
                                            marginLeft: "4px",
                                            marginTop: "10px",
                                          }}
                                        >
                                          {item.category_data?.category_name}
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "20px",
                                            marginLeft: "4px",
                                            marginTop: "5px",
                                            clear: "both",
                                          }}
                                        >
                                          <div
                                            style={{
                                              marginTop: "-6px",
                                              clear: "both",
                                            }}
                                            dangerouslySetInnerHTML={{
                                              __html: item.rich_text_content,
                                            }}
                                          />
                                        </div>
                                      </div>
                                    );
                                  }
                                  case "video": {
                                    const globalVideoNumber = videoCounter++; // ✅ use then increment
                                    const label = (
                                      <HeadingWithTreePath
                                        title={`Video ${globalVideoNumber}`}
                                        treePathParams={{
                                          sectionIndex: index,
                                          stepIndex: globalStepNumber - 1,
                                          substepIndex: substepIndex,
                                          nodeType: "video",
                                          nodeIndex: globalVideoNumber - 1,
                                        }}
                                      />
                                    );

                                    return (
                                      <div
                                        key={`substep-video-${substepId}-${globalVideoNumber}`}
                                        style={{
                                          marginTop: "20px",
                                          clear: "both",
                                        }}
                                      >
                                        <label
                                          style={{
                                            display: "flex",
                                            gap: "6px",
                                            cursor: "pointer",
                                            clear: "both",
                                          }}
                                        >
                                          {location.state.fromStepId ===
                                            stepId && (
                                            <input
                                              type="checkbox"
                                              checked={true}
                                              disabled={true}
                                              style={{
                                                accentColor: "#444444",
                                                backgroundColor: "#444444",
                                              }}
                                            />
                                          )}
                                          {label}
                                        </label>
                                        {renderVideo(item)}

                                        <div>{item.video_url}</div>

                                        <div>{item.short_description}</div>
                                      </div>
                                    );
                                  }

                                  case "form": {
                                    const globalFormNumber = formCounter++; // ✅ use then increment
                                    const label = (
                                      <HeadingWithTreePath
                                        title={`Form ${globalFormNumber}`}
                                        treePathParams={{
                                          sectionIndex: index,
                                          stepIndex: globalStepNumber - 1,
                                          substepIndex: substepIndex,
                                          nodeType: "form",
                                          nodeIndex: globalFormNumber - 1,
                                        }}
                                      />
                                    );
                                    // render form block here
                                    return (
                                      <div
                                        key={`substep-form-${substepId}-${globalFormNumber}`}
                                        style={{ clear: "both" }}
                                      >
                                        <label
                                          style={{
                                            display: "flex",
                                            gap: "6px",
                                            cursor: "pointer",
                                            marginTop: "20px",
                                            clear: "both",
                                          }}
                                        >
                                          {location.state.fromStepId ===
                                            stepId && (
                                            <input
                                              type="checkbox"
                                              checked={true}
                                              disabled={true}
                                              style={{
                                                accentColor: "#444444",
                                                backgroundColor: "#444444",
                                              }}
                                            />
                                          )}
                                          {label}
                                        </label>
                                        <div>{item.short_description}</div>
                                      </div>
                                    );
                                  }
                                  case "canvas": {
                                    const pages = getPages(item);

                                    const globalCanvasNumber = canvasCounter++; // ✅ use then increment
                                    const label = (
                                      <HeadingWithTreePath
                                        title={`Canvas ${globalCanvasNumber}`}
                                        treePathParams={{
                                          sectionIndex: index,
                                          stepIndex: globalStepNumber - 1,
                                          substepIndex: substepIndex,
                                          nodeType: "canvas",
                                          nodeIndex: globalCanvasNumber - 1,
                                        }}
                                      />
                                    );
                                    const shapes = item.shapes || [];

                                    return (
                                      <div
                                        key={`substep-canvas-${substepId}-${globalCanvasNumber}`}
                                        style={{
                                          marginTop: "40px",
                                          clear: "both",
                                        }}
                                      >
                                        <label
                                          style={{
                                            display: "flex",
                                            gap: "6px",
                                            cursor: "pointer",
                                            clear: "both",
                                          }}
                                        >
                                          {location.state.fromStepId ===
                                            stepId && (
                                            <input
                                              type="checkbox"
                                              checked={true}
                                              disabled={true}
                                              style={{
                                                accentColor: "#444444",
                                                backgroundColor: "#444444",
                                              }}
                                            />
                                          )}
                                          {label}
                                        </label>

                                        <div>{item.short_description}</div>

                                        {/* Display all pages vertically */}
                                        <CanvasDisplay pages={pages} />
                                      </div>
                                    );
                                  }
                                  case "attachment": {
                                    const attachmentList =
                                      item.attachments || [];
                                    if (attachmentList.length === 0)
                                      return null;

                                    // Increment counter for each attachment
                                    const globalAttachmentNumber =
                                      attachmentCounter++;
                                    const label = (
                                      <HeadingWithTreePath
                                        title={`Attachment ${globalAttachmentNumber}`}
                                        treePathParams={{
                                          sectionIndex: index,
                                          stepIndex: globalStepNumber - 1,
                                          substepIndex: substepIndex,
                                          nodeType: "attachment",
                                          nodeIndex: globalAttachmentNumber - 1,
                                        }}
                                      />
                                    );

                                    return (
                                      <div
                                        key={`substep-attachment-${substepId}-${globalAttachmentNumber}`}
                                        style={{ clear: "both" }}
                                      >
                                        <label
                                          style={{
                                            display: "flex",
                                            gap: "6px",
                                            cursor: "pointer",
                                            marginTop: "20px",
                                            clear: "both",
                                          }}
                                        >
                                          {location.state.fromStepId ===
                                            stepId && (
                                            <input
                                              type="checkbox"
                                              checked={true}
                                              disabled={true}
                                              style={{
                                                accentColor: "#444444",
                                                backgroundColor: "#444444",
                                              }}
                                            />
                                          )}
                                          {label}
                                        </label>
                                        <div>{item.short_description}</div>
                                        {attachmentList.map((attachment, i) => (
                                          <div
                                            key={`substep-attachment-item-${substepId}-${globalAttachmentNumber}-${i}`}
                                            style={{
                                              marginLeft: "20px",
                                              clear: "both",
                                            }}
                                          >
                                            {renderPreview(attachment)}
                                          </div>
                                        ))}
                                      </div>
                                    );
                                  }
                                  default:
                                    return null;
                                }
                              })}
                            </div>
                          );
                        }
                      )}
                    </div>
                  );
                }
              )}
            </div>
          );
        })}

      {snackbar.open && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={handleCloseSnackbar}
        />
      )}
      <div style={{ height: "100px" }}></div>
    </div>
  );
};
export default ProcedureStepMove;
